import { instance } from '@/apis/core';

import {
  CreateOrderParameters,
  GetOrdersResponse,
  OrderDeliveryResponse,
  OrderSheetParameter,
  OrderSheetResponse,
  OrdersDetailResponse,
  OrderCalculateRefundResponse,
  OrderGroup,
} from './types';

interface OrderRepositoryImpl {
  get(order_number: string, loggedIn: boolean, phone: number): Promise<OrdersDetailResponse>;
  orderGroups(): Promise<OrderGroup[]>;
  sheet(parameters: OrderSheetParameter): Promise<OrderSheetResponse>;
  list(search: string): Promise<GetOrdersResponse>;
  check(
    parameters: CreateOrderParameters,
  ): Promise<{ data: { order_number: string; total_price: number }; status: number }>;
  calculateRefund(
    orderNumber: string,
    cancel_data: { orderline_number: string; quantity: number }[],
  ): Promise<{ status: number; data: OrderCalculateRefundResponse }>;
  getShippingType(postcode: string): Promise<{ shipping_type: 'general' | 'remote' | 'jeju' }>;
}

class OrderRepository implements OrderRepositoryImpl {
  sheet = async (
    parameters: OrderSheetParameter,
    version?: string,
  ): Promise<OrderSheetResponse> => {
    const { data } = await instance.post(version ? `/${version}/orders/sheet` : `/orders/sheet`, {
      ...parameters,
    });

    return data;
  };
  list = async (search: string): Promise<GetOrdersResponse> => {
    const { data } = await instance.get(`/v2/customer/orders?keyword=${search}`);
    return data;
  };

  check = async (parameters: CreateOrderParameters, version?: string) => {
    const { data, status } = await instance.post(version ? '/v2/orders' : '/orders', parameters);
    return { data, status };
  };
  calculateRefund = async (
    orderNumber: string,
    cancel_data: { orderline_number: string; quantity: number }[],
  ): Promise<{ status: number; data: OrderCalculateRefundResponse }> => {
    const { status, data } = await instance.post(`/v2/payments/${orderNumber}/cancel-amount`, {
      cancel_data,
    });

    return { status, data };
  };
  cancelOrder = async ({
    isMember,
    order_number,
    cancel_data,
    cancel_amount,
    phone,
  }: {
    isMember: boolean;
    order_number: string;
    cancel_data: { orderline_number: string; quantity: number }[];
    cancel_amount: number;
    phone?: number;
  }) => {
    const path = isMember
      ? `/v2/payments/${order_number}/customer/refund`
      : `/v2/payments/${order_number}/annonymous/refund?phone=${phone}`;
    const { status } = await instance.delete(path, {
      data: {
        cancel_data,
        cancel_amount,
      },
    });

    return { status };
  };
  editDelivery = async (
    loggedIn: boolean,
    orderNumber: string,
    shippingInfo: OrderDeliveryResponse,
    phone?: number,
  ) => {
    const params = phone ? { phone } : undefined;
    const path = loggedIn ? '/v2/customer' : '/v2/annonymous';

    const { status, data } = await instance.put(
      `${path}/order-groups/${orderNumber}`,
      {
        ...shippingInfo,
      },
      {
        params,
      },
    );
    return { status, data };
  };
  get = async (
    order_group_number: string,
    loggedIn: boolean,
    phone?: number | string,
  ): Promise<OrdersDetailResponse> => {
    const path = loggedIn ? '/v2/customer/order-groups' : '/v2/annonymous/order-groups';

    const { data } = await instance.get(`${path}/${order_group_number}`, {
      params: {
        phone: phone ? phone : null,
      },
    });

    return data;
  };
  getV1 = async (
    order_group_number: string,
    loggedIn: boolean,
    phone?: number | string,
  ): Promise<OrdersDetailResponse> => {
    const path = loggedIn ? '/customer/order-groups' : '/annonymous/order-groups';

    const { data } = await instance.get(`${path}/${order_group_number}`, {
      params: {
        phone: phone ? phone : null,
      },
    });

    return data;
  };
  orderGroups = async (keyword?: string): Promise<OrderGroup[]> => {
    const { data } = await instance.get(`/v2/customer/order-groups?keyword=${keyword}`);
    return data;
  };
  getShippingType = async (
    postcode: string,
  ): Promise<{ shipping_type: 'general' | 'remote' | 'jeju' }> => {
    try {
      const { data } = await instance.get(`/v2/shipping-types?postcode=${postcode}`);
      return data;
    } catch (error) {
      return { shipping_type: 'general' };
    }
  };
}

export const orders = new OrderRepository();
